import React, { useCallback, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { UserLoggedInNavigatorScreenProps } from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { WidgetScreen } from "../../common/WidgetScreen";

import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { useWidgetEmployeeLeaveSchedulePreviewQueryLoader } from "./useWidgetEmployeeLeaveSchedulePreviewQueryLoader";
import { WidgetEmployeeLeavePreview } from "./WidgetEmployeeLeavePreview";
import { WSEmployeeLeaveQuery } from "./__generated__/WSEmployeeLeaveQuery.graphql";
import { WidgetEmployeeLeaveSettingsNavigator } from "./WidgetEmployeeLeaveSettingsNavigator";
import { WidgetSettingsEmployeeLeaveContextProvider } from "./WidgetEmployeeLeaveSettingsContext";
import { WidgetScreenPreview } from "../WidgetScreenPreview/WidgetScreenPreview";

type Props = UserLoggedInNavigatorScreenProps<"WSEmployeeLeave">;

export const WSEmployeeLeave = withWebModal(WSEmployeeLeaveC, true);
function WSEmployeeLeaveC(props: Props) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerWSEmployeeLeave {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

const WS_EMPLOYEE_LEAVE_QUERY = graphql`
  query WSEmployeeLeaveQuery($id: ID!) {
    widgetEmployeeLeave(id: $id) {
      id
      teams {
        id
      }
      whitelistLeaveReasons {
        id
      }
    }
  }
`;

function InnerWSEmployeeLeave(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<WSEmployeeLeaveQuery>(
    WS_EMPLOYEE_LEAVE_QUERY,
    {
      id: props.route.params.widgetId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const [teamIds, setTeamIds] = useState<string[]>(
    data.widgetEmployeeLeave?.teams.map(({ id }) => id) ?? []
  );
  const [leaveReasonIds, setLeaveReasonIds] = useState<string[]>(
    data.widgetEmployeeLeave?.whitelistLeaveReasons?.map(({ id }) => id) ?? []
  );
  const query = useWidgetEmployeeLeaveSchedulePreviewQueryLoader({
    storecastId: props.route.params.storecastId,
    teamIds,
    leaveReasonIds,
  });

  const [saveWidgetEmployeeLeave, loading] = useMutation(graphql`
    mutation WSEmployeeLeaveMutation(
      $storecastId: ID!
      $widgetId: ID!
      $input: UpdateWidgetEmployeeLeaveInput!
    ) {
      dashboard(dashboardId: $storecastId) {
        updateWidgetEmployeeLeave(input: $input, widgetId: $widgetId) {
          id
          teams {
            id
          }
          whitelistLeaveReasons {
            id
          }
        }
      }
    }
  `);

  const onSave = useCallback(() => {
    saveWidgetEmployeeLeave({
      variables: {
        storecastId: props.route.params.storecastId,
        widgetId: props.route.params.widgetId,
        input: {
          setTeamIds: teamIds,
          setWhitelistLeaveReasonIds: leaveReasonIds,
        },
      },
      onCompleted: props.navigation.goBack,
      onError: (e) => {
        console.log("e", e);
      },
    });
  }, [
    props.navigation,
    props.route.params.storecastId,
    props.route.params.widgetId,
    saveWidgetEmployeeLeave,
    teamIds,
    leaveReasonIds,
  ]);

  return (
    <WidgetScreen
      preview={
        <WidgetScreenPreview
          onSave={onSave}
          loading={loading}
          unitSize={props.route.params.unitSize}
          size={props.route.params.size}
        >
          {query ? (
            <WidgetEmployeeLeavePreview
              storecastId={props.route.params.storecastId}
              query={query}
            />
          ) : null}
        </WidgetScreenPreview>
      }
      settings={
        <WidgetSettingsEmployeeLeaveContextProvider
          value={{
            leaveReasonIds,
            setLeaveReasonIds,
            teamIds,
            setTeamIds,
            storecastId: props.route.params.storecastId,
          }}
        >
          <WidgetEmployeeLeaveSettingsNavigator
            onCancel={() => {
              props.navigation.goBack();
            }}
          />
        </WidgetSettingsEmployeeLeaveContextProvider>
      }
    />
  );
}
